import Breadcrumbs from '../components/Breadcrumbs';
import Link from '../components/Link';
import AdSlot from '../components/AdSlot';
import ReactionArticle from '../components/ReactionArticle';
import NotFoundPage from './NotFoundPage';
import { CONTENT_CLASSES } from '../constants/classNames';
import { ADSENSE_SLOTS, ASSISTANT_NAME, ROUTES } from '../constants/config';
import { findReactionPageBySlug, getReactionPages } from '../data/sitePages';
import { findReactionArticle } from '../data/reactionArticles';
import { buildElementPath, buildLabPath } from '../utils/routeUtils';
import { joinClassNames } from '../utils/classNameUtils';
import '../styles/ContentPage.css';

const MORE_REACTIONS_COUNT = 6;

function SubstanceLink({ substance }) {
  return substance.isElement() ? <Link to={buildElementPath(substance)}>{substance.name}</Link> : <>{substance.name}</>;
}

/**
 * One page per famous reaction, with the hand-written explanation and its long article.
 */
export default function ReactionPage({ slug }) {
  const page = findReactionPageBySlug(slug);
  if (!page) return <NotFoundPage />;
  const { reaction, first, second, path } = page;
  const article = findReactionArticle(reaction.reactantIds);
  const moreReactions = getReactionPages()
    .filter((other) => other.slug !== slug)
    .slice(0, MORE_REACTIONS_COUNT);

  return (
    <article className={CONTENT_CLASSES.root}>
      <Breadcrumbs
        trail={[
          { label: 'Lab', path: ROUTES.LAB },
          { label: 'Famous reactions', path: ROUTES.REACTIONS },
          { label: `${first.name} + ${second.name}`, path },
        ]}
      />

      <header className={CONTENT_CLASSES.hero}>
        <p className={CONTENT_CLASSES.meta}>
          What happens when you mix <SubstanceLink substance={first} /> and <SubstanceLink substance={second} />?
        </p>
        <h1 className={CONTENT_CLASSES.heading}>{reaction.title}</h1>
        <p className={CONTENT_CLASSES.lead}>{reaction.explanation}</p>
        <div className={CONTENT_CLASSES.buttonRow}>
          <Link to={buildLabPath(reaction.reactantIds)} className={CONTENT_CLASSES.button}>
            Try it in the lab 🧪
          </Link>
        </div>
      </header>

      <section className={CONTENT_CLASSES.section}>
        <h2 className={CONTENT_CLASSES.sectionTitle}>The result</h2>
        <dl className={CONTENT_CLASSES.factGrid}>
          <div className={CONTENT_CLASSES.fact}>
            <dt className={CONTENT_CLASSES.factLabel}>{reaction.isMixture ? 'What you get' : 'What it makes'}</dt>
            <dd className={CONTENT_CLASSES.factValue}>{reaction.productName}</dd>
          </div>
          {reaction.getEquation() && (
            <div className={CONTENT_CLASSES.fact}>
              <dt className={CONTENT_CLASSES.factLabel}>Equation</dt>
              <dd className={joinClassNames(CONTENT_CLASSES.factValue, CONTENT_CLASSES.equation)}>{reaction.getEquation()}</dd>
            </div>
          )}
          <div className={CONTENT_CLASSES.fact}>
            <dt className={CONTENT_CLASSES.factLabel}>When it happens</dt>
            <dd className={CONTENT_CLASSES.factValue}>{reaction.conditions}</dd>
          </div>
        </dl>
        {reaction.funFact && (
          <p className={CONTENT_CLASSES.funFact}>
            <strong>Fun fact from {ASSISTANT_NAME}:</strong> {reaction.funFact}
          </p>
        )}
        {reaction.safetyNote && <p className={CONTENT_CLASSES.safety}>🥽 {reaction.safetyNote}</p>}
      </section>

      {article && <ReactionArticle article={article} />}

      <section className={CONTENT_CLASSES.section}>
        <h2 className={CONTENT_CLASSES.sectionTitle}>More famous reactions</h2>
        <ul className={CONTENT_CLASSES.list}>
          {moreReactions.map((other) => (
            <li key={other.slug} className={CONTENT_CLASSES.listItem}>
              <Link to={other.path}>{other.reaction.title}</Link>
            </li>
          ))}
        </ul>
        <p className={CONTENT_CLASSES.text}>
          <Link to={ROUTES.REACTIONS}>See all famous reactions</Link>
        </p>
      </section>

      <AdSlot slotId={ADSENSE_SLOTS.PAGE_BOTTOM} />
    </article>
  );
}
